// src/pages/ChecklistAnalytics.js

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  CircularProgress,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Chip,
  Menu,
  ListItemIcon,
  Grid,
  Tooltip,
  FormHelperText,
  ButtonBase,
  Fade,
  Link,
  Container,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  RocketLaunch as RocketIcon,
  Autorenew as AutoIcon,
  Security as SecurityIcon,
  ContactSupport as SupportIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs, orderBy, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserData } from '../hooks/useUserData';
import ChecklistAnalysisListView from '../components/ChecklistAnalysisListView';
import { CALL_CENTER_QA_TEMPLATE } from '../constants/checklistTemplates';
import { ANALYSIS_MODELS, DEFAULT_ANALYSIS_MODEL_ID } from '../constants/analysisModels';

const formatChecklistUpdatedAt = (updatedAt) => {
  if (!updatedAt) {
    return '—';
  }

  let dateValue = null;

  if (updatedAt instanceof Date) {
    dateValue = updatedAt;
  } else if (typeof updatedAt?.toDate === 'function') {
    dateValue = updatedAt.toDate();
  } else if (typeof updatedAt?.seconds === 'number') {
    dateValue = new Date(updatedAt.seconds * 1000);
  }

  if (!dateValue || Number.isNaN(dateValue.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('hy-AM', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(dateValue);
};

const getQuestionTypeLabel = (type) =>
  ({ 'yes_no': 'Այո / Ոչ', 'yes_no_partially': 'Այո / Ոչ / Մասամբ', 'grade_1_10': 'Գնահատական' })[type] || '';

// Reusable component for inline text editing
const InlineEditField = ({ initialValue, onSave, label, typographyProps, multiline = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (value.trim() !== initialValue.trim()) {
      await onSave(value.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setValue(initialValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return isEditing ? (
    <Box sx={{ width: '100%' }}>
      <TextField
        fullWidth
        inputRef={inputRef}
        label={label}
        value={value}
        multiline={multiline}
        rows={multiline ? 3 : 1}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        variant="outlined"
        size="small"
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
        <IconButton
          size="small"
          onClick={handleSave}
          color="primary"
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleCancel}
          sx={{
            bgcolor: 'grey.300',
            '&:hover': { bgcolor: 'grey.400' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  ) : (
    <Tooltip title="Խմբագրել" placement="bottom-start">
      <Box onClick={() => setIsEditing(true)} sx={{ cursor: 'pointer', width: '100%' }}>
        <Typography {...typographyProps} sx={{ ...typographyProps.sx, whiteSpace: 'pre-wrap', minHeight: '1.5em' }}>
          {value || <span style={{ color: '#999' }}>{label}...</span>}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// Dialog for editing the context field in a large popup
const ContextEditDialog = ({ open, onClose, initialValue, onSave, label }) => {
  const [value, setValue] = useState(initialValue);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const isDirty = value.trim() !== (initialValue || '').trim();

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const handleSave = async () => {
    await onSave(value.trim());
    onClose();
  };

  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirm = () => {
    setConfirmDiscardOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '92vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle>Կոնտեքստ</DialogTitle>
        <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 0 }}>
          <TextField
            fullWidth
            autoFocus
            multiline
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={label}
            variant="outlined"
            sx={{ mt: 1, flex: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' }, '& textarea': { height: '100% !important', overflowY: 'auto !important', resize: 'none' } }}
            InputProps={{ sx: { height: '100%' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Չեղարկել</Button>
          <Button onClick={handleSave} variant="contained">Պահպանել</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDiscardOpen} onClose={() => setConfirmDiscardOpen(false)}>
        <DialogTitle>Պահպանե՞լ փոփոխությունները</DialogTitle>
        <DialogContent>
          <DialogContentText>Կատարված փոփոխությունները չեն պահպանվել։ Ցանկանու՞մ եք պահպանել դրանք։</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardConfirm}>Չեղարկել</Button>
          <Button onClick={() => { setConfirmDiscardOpen(false); handleSave(); }} variant="contained">Պահպանել</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Dialog for adding or editing a question
const QuestionEditorDialog = ({ open, onClose, onSave, onDelete, question }) => {
  const [text, setText] = useState('');
  const [type, setType] = useState('yes_no');
  const [guidance, setGuidance] = useState('');
  const isEditing = useMemo(() => !!question, [question]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setText(question?.text || '');
      setType(question?.type || 'yes_no');
      setGuidance(question?.guidance || '');
      // Focus after the dialog animation completes
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, question]);

  const handleSave = () => {
    onSave({ id: question?.id || uuidv4(), text: text.trim(), type, guidance: guidance.trim() });
    onClose();
  };

  const getQuestionTypeHelperText = (selectedType) => {
    if (selectedType === 'yes_no') {
      return 'Օգտագործել այս տեսակը միայն այն հարցերի համար, որտեղ պատասխանը կարող է լինել միայն "Այո" կամ "Ոչ"։';
    }
    if (selectedType === 'yes_no_partially') {
      return 'Օգտագործել այս տեսակը, երբ պատասխանը կարող է լինել «Այո», «Ոչ» կամ «Մասամբ»։';
    }
    return 'Օգտագործել այս տեսակը որակական գնահատման համար, երբ պատասխանը պետք է լինի 1-ից 10 միջակայքում։';
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>{isEditing ? 'Խմբագրել հարցը' : 'Ավելացնել նոր հարց'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <TextField
            inputRef={inputRef}
            label="Հարցի տեքստը"
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            helperText="Լավ հարցերը կոնկրետ են, միանշանակ և չեն պարունակում ենթադրություններ"
          />
          <FormControl fullWidth>
            <InputLabel>Հարցի տեսակը</InputLabel>
            <Select value={type} label="Հարցի տեսակը" onChange={(e) => setType(e.target.value)}>
              <MenuItem value="yes_no">Այո / Ոչ</MenuItem>
              <MenuItem value="yes_no_partially">Այո / Ոչ / Մասամբ</MenuItem>
              <MenuItem value="grade_1_10">Գնահատական (1-10)</MenuItem>
            </Select>
            <FormHelperText>{getQuestionTypeHelperText(type)}</FormHelperText>
          </FormControl>
          <TextField
            label="Գնահատման ուղեցույց (ըստ ցանկության)"
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            fullWidth
            variant="outlined"
            multiline
            rows={5}
            helperText="Կոնկրետ չափանիշ հենց այս հարցի համար՝ ի՞նչն է «լավ», ի՞նչը՝ խախտում, իսկ 1–10-ի դեպքում՝ գնահատականի սանդղակ (օր.՝ 9–10 / 5–8 / 1–4)։ Ձայնային հատկանիշները (տոն, դադարներ) transcript-ով չեն գնահատվում։"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px', justifyContent: 'space-between' }}>
        {isEditing ? (<Button onClick={() => onDelete(question.id)} color="error">Ջնջել հարցը</Button>) : (<Box />)}
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Չեղարկել</Button>
          <Button onClick={handleSave} variant="contained" disabled={!text.trim()}>Պահպանել</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const SortableQuestionItem = ({ q, index, total, getQuestionTypeLabel, onEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: q.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 'auto',
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ListItem
        button
        onClick={() => onEdit(q)}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
            '& .edit-icon': { opacity: 1 }
          },
          py: 2,
          px: { xs: 3, md: 4 },
          borderBottom: index < total - 1 ? '1px solid' : 'none',
          borderColor: 'divider'
        }}
      >
        <Box
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mr: 1,
            cursor: 'grab',
            color: 'text.disabled',
            '&:hover': { color: 'text.secondary' },
            touchAction: 'none',
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <ListItemText
          primary={q.text}
          primaryTypographyProps={{
            fontWeight: 500,
            lineHeight: 1.4
          }}
        />
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={getQuestionTypeLabel(q.type)}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontSize: '0.75rem'
            }}
          />
          <EditIcon
            className="edit-icon"
            color="primary"
            sx={{
              opacity: 0,
              transition: 'opacity 0.2s',
              fontSize: '18px'
            }}
          />
        </Stack>
      </ListItem>
    </div>
  );
};

// Main editor view for a single checklist
const ChecklistEditor = ({ checklist, onCancel, onUpdate, onDelete, onDuplicate, showSnackbar }) => {
  const [localChecklist, setLocalChecklist] = useState(JSON.parse(JSON.stringify(checklist)));
  const [isQuestionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [questionToEdit, setQuestionToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const menuOpen = Boolean(anchorEl);
  const [contextDialogOpen, setContextDialogOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const questions = localChecklist.questions || [];
    const oldIndex = questions.findIndex(q => q.id === active.id);
    const newIndex = questions.findIndex(q => q.id === over.id);
    const reordered = arrayMove(questions, oldIndex, newIndex);
    setLocalChecklist(prev => ({ ...prev, questions: reordered }));
    await onUpdate(checklist.id, { questions: reordered });
  };

  useEffect(() => {
    setLocalChecklist(JSON.parse(JSON.stringify(checklist)));
  }, [checklist]);

  const handleUpdateField = async (field, value) => {
    setLocalChecklist(prev => ({ ...prev, [field]: value }));
    await onUpdate(checklist.id, { [field]: value });
  };

  const handleOpenQuestionDialog = (question = null) => {
    setQuestionToEdit(question);
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = async (questionData) => {
    const existing = localChecklist.questions?.find(q => q.id === questionData.id);
    let updatedQuestions = existing
      ? localChecklist.questions.map(q => q.id === questionData.id ? questionData : q)
      : [...(localChecklist.questions || []), questionData];
    setLocalChecklist(prev => ({ ...prev, questions: updatedQuestions }));
    await onUpdate(checklist.id, { questions: updatedQuestions });
  };

  const handleDeleteQuestion = async (questionId) => {
    const updatedQuestions = localChecklist.questions.filter(q => q.id !== questionId);
    setLocalChecklist(prev => ({ ...prev, questions: updatedQuestions }));
    await onUpdate(checklist.id, { questions: updatedQuestions });
    setQuestionDialogOpen(false);
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(checklist.id);
    showSnackbar('ID-ն պատճենված է', 'success');
  };

  return (
    <>
      <Stack spacing={{ xs: 2, md: 3 }}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <IconButton
              onClick={onCancel}
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1, mx: { xs: 1, sm: 2 }, minWidth: 0 }}>
              <InlineEditField
                initialValue={localChecklist.name || ''}
                onSave={(v) => handleUpdateField('name', v)}
                label="Ցուցակի անվանում"
                typographyProps={{
                  variant: 'h5',
                  component: 'h1',
                  fontWeight: 600,
                  textAlign: 'left',
                  noWrap: true
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Վերջին թարմացում: {formatChecklistUpdatedAt(checklist.updatedAt)}
              </Typography>
            </Box>
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' }
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>

          <Stack spacing={3}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                Ստուգացուցակի Նկարագրություն
              </Typography>
              <InlineEditField
                initialValue={localChecklist.description || ''}
                onSave={(v) => handleUpdateField('description', v)}
                label="Նկարագրություն"
                typographyProps={{ variant: 'body1', color: 'text.secondary', lineHeight: 1.6 }}
                multiline
              />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                Կոնտեքստ
              </Typography>
              <Tooltip title="Խմբագրել" placement="bottom-start">
                <Box
                  onClick={() => setContextDialogOpen(true)}
                  sx={{ cursor: 'pointer', width: '100%' }}
                >
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6, minHeight: '1.5em', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {localChecklist.context || <span style={{ color: '#999' }}>Որքան շատ կոնտեքստ տաք, այնքան լավ որակի վերլուծություն կստացվի…</span>}
                  </Typography>
                </Box>
              </Tooltip>
              <ContextEditDialog
                open={contextDialogOpen}
                onClose={() => setContextDialogOpen(false)}
                initialValue={localChecklist.context || ''}
                onSave={(v) => handleUpdateField('context', v)}
                label="Որքան շատ կոնտեքստ տաք, այնքան լավ որակի վերլուծություն կստացվի։"
              />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                Վերլուծության մոդել
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={localChecklist.model || DEFAULT_ANALYSIS_MODEL_ID}
                  onChange={(e) => handleUpdateField('model', e.target.value)}
                >
                  {ANALYSIS_MODELS.map((m) => (
                    <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {(ANALYSIS_MODELS.find((m) => m.id === (localChecklist.model || DEFAULT_ANALYSIS_MODEL_ID)) || {}).description
                    || 'Մոդելը, որով կկատարվի այս ցուցակի վերլուծությունը։'}
                </FormHelperText>
              </FormControl>
            </Box>

            <Divider />

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                API ID
              </Typography>
              <Box
                onClick={handleCopyId}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    '& .copy-icon': {
                      opacity: 1
                    }
                  }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {checklist.id}
                  </Typography>
                  <ContentCopyIcon
                    className="copy-icon"
                    fontSize="small"
                    sx={{
                      opacity: 0.3,
                      transition: 'opacity 0.2s',
                      fontSize: '16px'
                    }}
                  />
                </Stack>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Այս ID-ն օգտագործեք API-ում։{' '}
                <Link
                  href="https://docs.xosum.am/api-reference/introduction"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'none' }}
                >
                  API փաստաթղթեր
                </Link>
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: { xs: 3, md: 4 }, pb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Հարցեր ({localChecklist.questions?.length || 0})
              </Typography>
              <Button
                variant="contained"
                size="medium"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleOpenQuestionDialog()}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Ավելացնել նոր հարց
              </Button>
            </Stack>
          </Box>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={(localChecklist.questions || []).map(q => q.id)} strategy={verticalListSortingStrategy}>
              <List disablePadding>
                {(localChecklist.questions || []).map((q, index) => (
                  <SortableQuestionItem
                    key={q.id}
                    q={q}
                    index={index}
                    total={localChecklist.questions?.length || 0}
                    getQuestionTypeLabel={getQuestionTypeLabel}
                    onEdit={handleOpenQuestionDialog}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        </Paper>
      </Stack>

      <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { onDuplicate(checklist.id); setAnchorEl(null); }}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          Կրկնօրինակել ցուցակը
        </MenuItem>
        <MenuItem onClick={() => { setDeleteDialogOpen(true); setAnchorEl(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          Ջնջել ցուցակը
        </MenuItem>
      </Menu>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Հաստատե՞լ ցուցակի ջնջումը</DialogTitle>
        <DialogContent>
          <DialogContentText paragraph>
            Սա անդառնալի գործողություն է։ Ցուցակը ջնջելուց հետո դուք այլևս չեք կարողանա այս ցուցակով նոր վերլուծություններ կատարել։ Նախկինում կատարված վերլուծությունների արդյունքները կպահպանվեն։
          </DialogContentText>
          <DialogContentText paragraph>
            Շարունակելու համար, խնդրում ենք մուտքագրել ցուցակի անունը՝ <strong>{localChecklist.name}</strong>
          </DialogContentText>
          <TextField autoFocus value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} fullWidth variant="outlined" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Չեղարկել</Button>
          <Button onClick={() => onDelete(checklist.id)} color="error" disabled={deleteConfirmation !== localChecklist.name}>
            Ես հասկանում եմ, ջնջել
          </Button>
        </DialogActions>
      </Dialog>

      <QuestionEditorDialog open={isQuestionDialogOpen} onClose={() => setQuestionDialogOpen(false)} onSave={handleSaveQuestion} onDelete={handleDeleteQuestion} question={questionToEdit} />
    </>
  );
};

// Component that displays the list of all checklists
const ChecklistList = ({ checklists, onSelect, onCreateNew, onUseTemplate }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
        AI Վերլուծություն
      </Typography>
      <Button
        variant="contained"
        size="large"
        startIcon={<AddCircleOutlineIcon />}
        onClick={onCreateNew}
        sx={{
          py: 1.5,
          px: 3,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600
        }}
      >
        Նոր ստուգացուցակ
      </Button>
    </Stack>
    {checklists.length > 0 ? (
      <Grid container spacing={3}>
        {checklists.map((c, index) => (
          <Grid item xs={12} sm={6} md={4} key={c.id}>
            <Fade in timeout={400} style={{ transitionDelay: `${index * 100}ms` }}>
              <Paper
                component={ButtonBase}
                onClick={() => onSelect(c.id)}
                elevation={1}
                sx={{
                  width: '100%',
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'left',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  borderRadius: 3,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    '& .question-count': {
                      bgcolor: 'primary.main',
                      color: 'white'
                    }
                  }
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
                    {c.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.5,
                      minHeight: '3em'
                    }}
                  >
                    {c.description || 'Նկարագրություն չկա'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Վերջին թարմացում: {formatChecklistUpdatedAt(c.updatedAt)}
                  </Typography>
                </Box>
                <Chip
                  className="question-count"
                  label={`${c.questions?.length || 0} հարց`}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    fontSize: '0.75rem'
                  }}
                />
              </Paper>
            </Fade>
          </Grid>
        ))}
      </Grid>
    ) : (
      <Box sx={{ maxWidth: 760, mx: 'auto', textAlign: 'center', py: { xs: 2, sm: 4 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Ստեղծեք ձեր առաջին ստուգացուցակը
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Ընտրեք՝ ինչպես սկսել։
        </Typography>

        <Grid container spacing={2.5} alignItems="stretch">
          {/* Recommended path: start from the ready-made template */}
          <Grid item xs={12} sm={6}>
            <Paper
              component={ButtonBase}
              onClick={onUseTemplate}
              elevation={0}
              sx={{
                width: '100%',
                height: '100%',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                borderRadius: 3,
                border: '2px solid',
                borderColor: 'primary.main',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { boxShadow: 6, transform: 'translateY(-3px)' },
              }}
            >
              <Chip
                label="Խորհուրդ ենք տալիս"
                size="small"
                color="primary"
                sx={{ position: 'absolute', top: 12, right: 12, borderRadius: 2, fontWeight: 600 }}
              />
              <Box
                sx={{
                  width: 56, height: 56, borderRadius: '50%',
                  bgcolor: 'primary.main', color: 'primary.contrastText',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, mt: 1,
                }}
              >
                <SparkleIcon />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Սկսել պատրաստի ձևանմուշով
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Զանգերի կենտրոնի QA ձևանմուշ՝ պատրաստի հարցերով։ Բացեք և խմբագրեք ըստ ձեր կարիքների։
              </Typography>
            </Paper>
          </Grid>

          {/* Secondary path: blank checklist */}
          <Grid item xs={12} sm={6}>
            <Paper
              component={ButtonBase}
              onClick={onCreateNew}
              elevation={0}
              sx={{
                width: '100%',
                height: '100%',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { boxShadow: 4, transform: 'translateY(-3px)', borderColor: 'text.secondary' },
              }}
            >
              <Box
                sx={{
                  width: 56, height: 56, borderRadius: '50%',
                  bgcolor: 'action.hover', color: 'text.secondary',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, mt: 1,
                }}
              >
                <AddCircleOutlineIcon />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Սկսել զրոյից
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ստեղծեք դատարկ ստուգացուցակ և ավելացրեք ձեր սեփական հարցերը։
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    )}
  </Box>
);

// Main component, renamed for clarity, but the export remains the same.
const ChecklistManager = () => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChecklistId, setSelectedChecklistId] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [editReturnView, setEditReturnView] = useState('analysis');
  const [checklistLimitDialogOpen, setChecklistLimitDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const navigate = useNavigate();
  const { checklistId: routeChecklistId } = useParams();
  const { userData, loadingUserData } = useUserData();
  const hasBusinessPlan = userData?.plan?.toLowerCase() === 'business';
  const selectedChecklist = useMemo(
    () => checklists.find((c) => c.id === selectedChecklistId) || null,
    [checklists, selectedChecklistId]
  );
  const resolvedChecklistLimit = useMemo(() => {
    const rawLimit = Number(userData?.checklist_limit);
    if (Number.isFinite(rawLimit) && Number.isInteger(rawLimit) && rawLimit >= 1) {
      return rawLimit;
    }
    return 1;
  }, [userData?.checklist_limit]);
  const currentChecklistCount = checklists.length;
  const isChecklistLimitReached = currentChecklistCount >= resolvedChecklistLimit;
  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });
  const openChecklistContactForm = () => {
    const email = auth.currentUser?.email ?? '';
    window.open(
      `https://tally.so/r/3Evk5L?prefilled_email=${encodeURIComponent(email)}&source=checklist`,
      '_blank'
    );
  };

  const fetchChecklists = async () => {
    setLoading(true);
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    try {
      const q = query(collection(db, 'checklists'), where('userId', '==', user.uid), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setChecklists(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (error) { showSnackbar('Ցուցակները բեռնելիս առաջացավ սխալ', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // Only fetch checklists if user data has loaded and the user has a business plan.
    if (!loadingUserData && hasBusinessPlan) {
      fetchChecklists();
    }
  }, [loadingUserData, hasBusinessPlan]);

  useEffect(() => {
    if (routeChecklistId) {
      setSelectedChecklistId((prev) => (prev === routeChecklistId ? prev : routeChecklistId));
      if (viewMode !== 'edit') {
        setViewMode('analysis');
        setEditReturnView('analysis');
      }
      return;
    }

    if (viewMode !== 'edit') {
      setSelectedChecklistId('');
      setViewMode('list');
      setEditReturnView('list');
    }
  }, [routeChecklistId, viewMode]);

  useEffect(() => {
    if (!selectedChecklistId || loading) return;
    const checklistExists = checklists.some((checklist) => checklist.id === selectedChecklistId);
    if (checklistExists) return;

    setSelectedChecklistId('');
    setViewMode('list');
    if (routeChecklistId) {
      showSnackbar('Ստուգացուցակը չի գտնվել', 'error');
      navigate('/ai-analysis', { replace: true });
    }
  }, [checklists, selectedChecklistId, loading, routeChecklistId, navigate]);

  const createChecklist = async (seed) => {
    const user = auth.currentUser;
    if (!user) return;
    if (isChecklistLimitReached) {
      setChecklistLimitDialogOpen(true);
      return;
    }
    const now = new Date();
    const newChecklistData = {
      name: seed.name,
      description: seed.description ?? '',
      context: seed.context ?? '',
      questions: seed.questions ?? [],
      model: seed.model ?? DEFAULT_ANALYSIS_MODEL_ID,
      userId: user.uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    };
    try {
      const docRef = await addDoc(collection(db, 'checklists'), newChecklistData);
      const createdChecklist = {
        id: docRef.id,
        name: newChecklistData.name,
        description: newChecklistData.description,
        context: newChecklistData.context,
        questions: newChecklistData.questions,
        model: newChecklistData.model,
        userId: newChecklistData.userId,
        createdAt: now,
        updatedAt: now,
      };
      setChecklists((prev) => [createdChecklist, ...prev]);
      setSelectedChecklistId(docRef.id);
      setEditReturnView('list');
      setViewMode('edit');
      navigate(`/ai-analysis/${docRef.id}`);
      fetchChecklists();
    } catch (error) { showSnackbar('Նոր ցուցակ ստեղծելիս առաջացավ սխալ', 'error'); }
  };

  const handleCreateNew = () => createChecklist({ name: 'Անվերնագիր ցուցակ' });

  const handleCreateFromTemplate = () => createChecklist({
    name: CALL_CENTER_QA_TEMPLATE.name,
    description: CALL_CENTER_QA_TEMPLATE.description,
    context: CALL_CENTER_QA_TEMPLATE.context,
    questions: CALL_CENTER_QA_TEMPLATE.questions.map((q) => ({ ...q, id: uuidv4() })),
  });

  const handleDuplicateChecklist = async (id) => {
    const source = checklists.find((c) => c.id === id);
    if (!source) return;
    await createChecklist({
      name: `${source.name} (պատճեն)`,
      description: source.description ?? '',
      context: source.context ?? '',
      questions: (source.questions || []).map((q) => ({ ...q, id: uuidv4() })),
      model: source.model ?? DEFAULT_ANALYSIS_MODEL_ID,
    });
  };

  const handleUpdateChecklist = async (id, data) => {
    try {
      const now = new Date();
      await updateDoc(doc(db, 'checklists', id), { ...data, updatedAt: serverTimestamp() });
      // Optimistic update on frontend for responsiveness
      setChecklists(prev => prev.map(c => c.id === id ? { ...c, ...data, updatedAt: now } : c));
    } catch (error) { showSnackbar('Ցուցակը թարմացնելիս առաջացավ սխալ', 'error'); }
  };

  const handleDeleteChecklist = async (id) => {
    try {
      await deleteDoc(doc(db, 'checklists', id));
      showSnackbar('Ցուցակը հաջողությամբ ջնջվեց', 'success');
      setChecklists((prev) => prev.filter((checklist) => checklist.id !== id));
      if (id === selectedChecklistId) {
        setSelectedChecklistId('');
        setViewMode('list');
        navigate('/ai-analysis', { replace: true });
      }
      fetchChecklists();
    } catch (error) { showSnackbar('Ցուցակը ջնջելիս առաջացավ սխալ', 'error'); }
  };

  const handleSelectChecklist = (id) => {
    setSelectedChecklistId(id);
    setEditReturnView('analysis');
    setViewMode('analysis');
    navigate(`/ai-analysis/${id}`);
  };

  const handleBackFromAnalysis = () => {
    setSelectedChecklistId('');
    setViewMode('list');
    navigate('/ai-analysis');
  };

  const handleOpenEditor = () => {
    if (!selectedChecklistId) return;
    setEditReturnView('analysis');
    setViewMode('edit');
  };

  const handleEditorCancel = () => {
    if (editReturnView === 'list') {
      setSelectedChecklistId('');
      setViewMode('list');
      navigate('/ai-analysis');
      return;
    }
    setViewMode(editReturnView);
    if (selectedChecklistId) {
      navigate(`/ai-analysis/${selectedChecklistId}`);
    }
  };

  if (loadingUserData) { // Simplified loading check
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="h6" color="text.secondary">Բեռնվում է…</Typography>
          </Stack>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      {!hasBusinessPlan ? (
        <Fade in timeout={600}>
          <Paper
            sx={{
              p: 4,
              textAlign: 'center',
              mb: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Background decoration */}
            <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', opacity: 0.5 }} />
            <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', opacity: 0.7 }} />

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ mb: 3 }}>
                <RocketIcon sx={{ fontSize: 56, mb: 2, opacity: 0.9 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
                  QA Ստուգացուցակները հասանելի են Business պլանում
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, maxWidth: 600, mx: 'auto' }}>
                  Ստեղծեք հարցաշարեր և ավտոմատ կերպով ստուգեք ձեր զանգերի կենտրոնի օպերատորների աշխատանքի որակը։
                </Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <SparkleIcon sx={{ fontSize: 24, mb: 1, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                      Ավտոմատ վերլուծություն
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <AutoIcon sx={{ fontSize: 24, mb: 1, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                      Ճկուն հարցաշարեր
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <SecurityIcon sx={{ fontSize: 24, mb: 1, opacity: 0.8 }} />
                    <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                      Որակի վերահսկողություն
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.95)',
                  color: '#667eea',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: 'white',
                    boxShadow: '0 6px 25px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  const email = auth.currentUser?.email ?? '';
                  window.open(
                    `https://tally.so/r/3Evk5L?prefilled_email=${encodeURIComponent(email)}&source=checklist_request`,
                    '_blank'
                  );
                }}
                startIcon={<SupportIcon />}
              >
                Կապ մեզ հետ
              </Button>
            </Box>
          </Paper>
        </Fade>
      ) : (
        <>
          {loading && (viewMode === 'list' || (selectedChecklistId && !selectedChecklist)) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : viewMode === 'edit' && selectedChecklist ? (
            <ChecklistEditor checklist={selectedChecklist} onCancel={handleEditorCancel} onUpdate={handleUpdateChecklist} onDelete={handleDeleteChecklist} onDuplicate={handleDuplicateChecklist} showSnackbar={showSnackbar} />
          ) : viewMode === 'analysis' && selectedChecklist ? (
            <ChecklistAnalysisListView
              checklist={selectedChecklist}
              onBack={handleBackFromAnalysis}
              onEditChecklist={handleOpenEditor}
            />
          ) : (
            <ChecklistList checklists={checklists} onSelect={handleSelectChecklist} onCreateNew={handleCreateNew} onUseTemplate={handleCreateFromTemplate} />
          )}
        </>
      )}
      <Dialog
        open={checklistLimitDialogOpen}
        onClose={() => setChecklistLimitDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          }
        }}
      >
        {/* Hero section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            pt: 4,
            pb: 3,
            px: 3,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.07)' }} />
          <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.15)',
                border: '2px solid rgba(255,255,255,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <SparkleIcon sx={{ fontSize: 36, color: 'white' }} />
            </Box>
            <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: '2.75rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>
                {currentChecklistCount}
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', color: 'rgba(255,255,255,0.5)', fontWeight: 400, lineHeight: 1 }}>
                /{resolvedChecklistLimit}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
              ստուգացուցակները օգտագործված են
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
            Ավելացրեք ստուգացուցակների քանակը
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2.5, lineHeight: 1.6 }}>
            Կապվեք մեզ հետ և ստացեք ձեր թիմի կարիքներին հարմար լուծում։
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="center">
            <Chip icon={<CheckIcon />} label="Ավելի արդյունավետ" size="small" color="success" variant="outlined" />
            <Chip icon={<AutoIcon />} label="Արագ ակտիվացում" size="small" color="primary" variant="outlined" />
            <Chip icon={<SecurityIcon />} label="Անհատական պայմաններ" size="small" color="secondary" variant="outlined" />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2, flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<SupportIcon />}
            onClick={() => {
              setChecklistLimitDialogOpen(false);
              openChecklistContactForm();
            }}
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 15px rgba(102,126,234,0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4290 100%)',
                boxShadow: '0 6px 20px rgba(102,126,234,0.5)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Կապ մեզ հետ
          </Button>
          <Button
            onClick={() => setChecklistLimitDialogOpen(false)}
            fullWidth
            sx={{ textTransform: 'none', color: 'text.secondary', py: 0.75 }}
          >
            Փակել
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChecklistManager;
